// src/components/PainelLideranca.js
import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import '../styles/Colaborador.css';
import '../styles/Organograma.css';
import { arrayMove } from '@dnd-kit/sortable';
import DetalhesModal from './Shared/DetalhesModal';

// --- Funções Auxiliares ---
const formatCurrency = (value) => {
    if (typeof value !== 'number') return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};
const formatDate = (dateInput) => {
    if (!dateInput) return '-';
    try {
        const date = dateInput.toDate ? dateInput.toDate() : new Date(dateInput);
        if (isNaN(date.getTime())) return '-';
        return new Intl.DateTimeFormat('pt-BR').format(date);
    } catch (e) { return '-'; }
};
const getUltimoRecebimento = (historico) => {
    if (!historico || historico.length === 0) return '-';
    try {
        const sortedHistorico = [...historico].sort((a, b) => (b.data.toDate ? b.data.toDate() : new Date(b.data)) - (a.data.toDate ? a.data.toDate() : new Date(a.data)));
        return formatCurrency(sortedHistorico[0].salario); 
    } catch (e) {
        console.error("Erro ao processar último recebimento:", e);
        return 'Erro';
    }
};

const PainelLideranca = () => {
  const [subordinados, setSubordinados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [selectedSubordinate, setSelectedSubordinate] = useState(null);
  const [liderWidgetOrder, setLiderWidgetOrder] = useState(['humor', 'pdi', 'salario']);
  const [currentLeaderId, setCurrentLeaderId] = useState(null);

  const fetchData = useCallback(async (authUser) => {
    if (!authUser) { setLoading(false); setError("Autenticação necessária."); return; }
    setCurrentLeaderId(authUser.uid);
    setLoading(true);
    try {
        // 1. Busca dados do Líder (perfil de usuário)
        const userDocSnap = await getDoc(doc(db, 'users', authUser.uid));
        if (!userDocSnap.exists() || !userDocSnap.data().empresa_id) throw new Error("ID da empresa não encontrado para seu usuário.");
        
        const leaderData = userDocSnap.data();
        const defaultOrder = ['humor', 'pdi', 'salario'];
        const savedOrder = leaderData.liderWidgetOrder || defaultOrder;
        setLiderWidgetOrder(savedOrder);

        // 2. CORREÇÃO: Verifica a permissão no documento do USUÁRIO (onde o toggle do organograma salva)
        if (leaderData.canViewSubordinates !== true) { 
            setPermissionGranted(false); 
            setLoading(false); 
            return; 
        }
        setPermissionGranted(true);

        // 3. Busca a posição do líder no Organograma para encontrar subordinados
        const empresaId = leaderData.empresa_id;
        const orgNodesCollectionRef = collection(db, 'Empresas', empresaId, 'organizationChartNodes');
        const qLeaderNode = query(orgNodesCollectionRef, where("userId", "==", authUser.uid));
        const leaderNodeSnapshot = await getDocs(qLeaderNode);

        if (leaderNodeSnapshot.empty) {
            // Tem permissão marcada no user, mas não está no organograma
            setSubordinados([]);
            setLoading(false);
            return;
        }

        // 4. Busca recursiva de subordinados (diretos e indiretos)
        const allSubordinateUserIds = new Set();
        const nodesToProcess = [leaderNodeSnapshot.docs[0].id];
        const processedNodes = new Set();

        while (nodesToProcess.length > 0) {
            const currentParentId = nodesToProcess.shift();
            if (processedNodes.has(currentParentId)) continue;
            processedNodes.add(currentParentId);
            const qChildren = query(orgNodesCollectionRef, where("parentId", "==", currentParentId));
            const childrenSnapshot = await getDocs(qChildren);
            childrenSnapshot.forEach(childDoc => {
                if (childDoc.data().userId) allSubordinateUserIds.add(childDoc.data().userId);
                nodesToProcess.push(childDoc.id);
            });
        }
        if (allSubordinateUserIds.size === 0) { setLoading(false); setSubordinados([]); return; }
        
        const subordinatePromises = Array.from(allSubordinateUserIds).map(async (userId) => {
            const [publicDocSnap, privateDocSnap] = await Promise.all([ getDoc(doc(db, 'users', userId)), getDoc(doc(db, 'usersprivado', userId)) ]);
            return publicDocSnap.exists() ? { uid: userId, publicData: publicDocSnap.data(), privateData: privateDocSnap.exists() ? privateDocSnap.data() : {} } : null;
        });

        const results = (await Promise.all(subordinatePromises)).filter(Boolean);
        results.sort((a, b) => a.publicData.name.localeCompare(b.publicData.name));
        setSubordinados(results);

    } catch (err) {
        console.error("Erro ao carregar painel de liderança:", err);
        setError(`Falha ao carregar dados da equipe: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { const unsubscribe = auth.onAuthStateChanged(fetchData); return () => unsubscribe(); }, [fetchData]);

  const saveLiderWidgetOrder = async (orderIds) => {
    if (!currentLeaderId) return;
    try { 
      await updateDoc(doc(db, 'users', currentLeaderId), { liderWidgetOrder: orderIds }); 
    } 
    catch (error) { 
      console.error("Erro ao salvar ordem dos widgets:", error); 
    }
  };
  
  const handleOrderChangeModal = (newOrder) => {
    setLiderWidgetOrder(newOrder);
    saveLiderWidgetOrder(newOrder);
  };

  const handleCloseModal = () => setSelectedSubordinate(null);
  
  if (loading) { return <div className="loading-spinner">Carregando dados da equipe...</div>; }
  if (error) { return <div className="error-message">{error}</div>; }
  if (!permissionGranted) { return <div className="info-message">Você não tem permissão para visualizar dados de liderados.</div>; }
  if (subordinados.length === 0) { return <div className="info-message">Nenhum liderado encontrado na sua equipe.</div>; }

   return (
    <div className="colaborador-wrapper">
      <div className="colaborador-header">
        <h2>Painel da Minha Equipe</h2>
        <p>Visualize as informações chave dos seus liderados diretos e indiretos.</p>
      </div>
      
      <div className="tabela-equipe-wrapper">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Função</th>
                <th>Último Recebimento</th> 
                <th>Data de Entrada</th>
                <th>Data de Inativação</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {subordinados.map(sub => (
                <tr key={sub.uid}>
                  <td data-label="Nome">{sub.publicData.name || 'N/A'}</td>
                  <td data-label="Função">{sub.publicData.funcao || 'N/A'}</td>
                  <td data-label="Último Recebimento">{getUltimoRecebimento(sub.privateData?.historicoSalarial)}</td>
                  <td data-label="Data de Entrada">{formatDate(sub.privateData?.dataEntrada)}</td>
                  <td data-label="Data de Inativação">{formatDate(sub.privateData?.dataInativacao)}</td>
                  <td className="text-center">
                    <button className="btn-detalhes" onClick={() => setSelectedSubordinate(sub)}>Ver Detalhes</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSubordinate && (
        <DetalhesModal 
            subordinado={selectedSubordinate} 
            onClose={handleCloseModal}
            widgetOrder={liderWidgetOrder}
            onOrderChange={handleOrderChangeModal}
        />
      )}
    </div>
  );
};

export default PainelLideranca;